import { useCallback, useEffect, useRef, useState } from 'react';
import { outcomeTotals } from '@root/lib';
import { TRANSITION } from '@root/lib/boardPath';
import { phaserBridge } from '@root/phaser/bridge';
import { journeySelector } from '@root/store/slices/journeySlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import type {
  CombinedRoundState,
  LudoJourneyStatus,
  Round,
} from '@root/store/types';

// An overshoot never moved, so the recorded position is where it started.
function startPosition(state: CombinedRoundState): number {
  const roll = state.currentRoll;
  if (!roll) return state.position;
  return roll.outcome === 'OVERSHOOT'
    ? roll.position
    : roll.position - roll.dice;
}

// Hold plus zoom, with room to spare. Derived so it can't go stale.
const INTRO_GUARD_MS = TRANSITION.preparingHoldMs + TRANSITION.zoomMs + 1500;
// Shuffle plus twelve hops, with room to spare.
const ROLL_GUARD_MS = 8000;
// Never leave the button disabled if a roll goes unanswered.
const SUBMIT_GUARD_MS = 10000;

export function usePhaserSync() {
  const { round } = useTypedSelector(roundSelector);
  const { colour, animatedRoundId, rollSubmitted, introPlayed } =
    useTypedSelector(journeySelector);
  const actions = useActions();
  const introGuard = useRef<number | undefined>(undefined);
  const rollGuard = useRef<number | undefined>(undefined);

  // State, not a ref: the round can arrive before the scene, and the effects
  // below have to run again once it does.
  const [sceneReady, setSceneReady] = useState(false);
  // Latest frame, so a scene that mounts late can be brought up to date.
  const latest = useRef<Round<CombinedRoundState> | undefined>(undefined);
  latest.current = round;
  // Which run's tiles are on screen.
  const paintedBoardFor = useRef<string | null>(null);
  // Which run has had its opening — intro or straight to the lane.
  const introducedRun = useRef<string | null>(null);
  // A finished run, held back until the pawn stops moving.
  const endedRound = useRef<{
    roundId: string;
    outcome: LudoJourneyStatus;
    totals: ReturnType<typeof outcomeTotals>;
  } | null>(null);
  // The roll we have played, not the round it arrived on: a run's first roll
  // settles the round that was already seeded, so the id alone can't tell a
  // played move from a fresh one.
  const animatedRoll = useRef<string | null>(null);
  // Runs whose card has been shown, so it can't open twice.
  const announced = useRef<string | null>(null);

  const paint = useCallback(
    (frame: Round<CombinedRoundState>) => {
      phaserBridge.sendToPhaser({ type: 'SET_COLOUR', colour });
      phaserBridge.sendToPhaser({
        type: 'SET_BOARD',
        tiles: frame.state.board ?? [],
      });
      phaserBridge.sendToPhaser({
        type: 'SET_POSITION',
        position: frame.state.position ?? 0,
      });
    },
    [colour]
  );

  useEffect(
    () =>
      phaserBridge.onPhaserEvent((event) => {
        switch (event.type) {
          case 'SCENE_READY': {
            setSceneReady(true);
            // Reconnect and first load both land here: redraw from the latest snapshot.
            if (latest.current) paint(latest.current);
            break;
          }
          case 'INTRO_COMPLETE':
            window.clearTimeout(introGuard.current);
            actions.setIntroPlayed(true);
            break;
          case 'ROLL_COMPLETE': {
            window.clearTimeout(rollGuard.current);
            actions.setRollInFlight(false);
            // Show the win or skull card only after the pawn has landed.
            const ended = endedRound.current;
            if (ended) {
              endedRound.current = null;
              actions.openRunResult(ended);
            }
            break;
          }
          default:
            break;
        }
      }),
    [actions, paint]
  );

  useEffect(() => {
    if (!rollSubmitted) return;
    const timer = window.setTimeout(
      () => actions.setRollSubmitted(false),
      SUBMIT_GUARD_MS
    );
    return () => window.clearTimeout(timer);
  }, [rollSubmitted, actions]);

  // Colour is cosmetic and can change between runs, so push it on its own.
  useEffect(() => {
    if (!sceneReady) return;
    phaserBridge.sendToPhaser({ type: 'SET_COLOUR', colour });
  }, [colour, sceneReady]);

  useEffect(() => {
    if (!round || !sceneReady) return;
    const state = round.state;
    if (!state) return;

    // Only redraw for a new run; an empty board would block the real one.
    const tiles = state.board ?? [];
    if (
      state.journeyId &&
      tiles.length > 0 &&
      state.journeyId !== paintedBoardFor.current
    ) {
      // A second run in the same session starts clean, intro and all.
      if (introducedRun.current && introducedRun.current !== state.journeyId) {
        actions.resetJourney();
      }
      paintedBoardFor.current = state.journeyId;
      phaserBridge.sendToPhaser({ type: 'SET_BOARD', tiles });
    }

    // The opening belongs to the run; the stake locks long after the tiles.
    const runStarted = (state.rollAmount ?? 0) > 0;
    if (
      state.journeyId &&
      runStarted &&
      state.journeyId !== introducedRun.current
    ) {
      // Already under way on the session's first frame: we missed the start.
      const resuming = animatedRoundId === null && !introducedRun.current;
      introducedRun.current = state.journeyId;
      // Resuming jumps to the lane; a run starting here gets the intro.
      if (resuming) {
        phaserBridge.sendToPhaser({ type: 'SHOW_LANE', colour });
        actions.setIntroPlayed(true);
      } else {
        phaserBridge.sendToPhaser({ type: 'PLAY_INTRO', colour });
        // Don't strand the player on a disabled "Preparing board" button.
        window.clearTimeout(introGuard.current);
        introGuard.current = window.setTimeout(
          () => actions.setIntroPlayed(true),
          INTRO_GUARD_MS
        );
      }
    }

    const roll = state.currentRoll;
    const rollKey = roll ? `${round.id}:${roll.position}:${roll.dice}` : null;
    const outcome =
      state.journeyStatus === 'WON' || state.journeyStatus === 'ELIMINATED'
        ? state.journeyStatus
        : null;

    // The server seeds the next run about a second later, so a result that
    // will never animate has to be shown as it lands or it is lost.
    const announce = () => {
      if (!outcome || announced.current === round.id) return;
      announced.current = round.id;
      actions.openRunResult({
        roundId: round.id,
        outcome,
        totals: outcomeTotals(state, outcome === 'WON'),
      });
    };

    // First frame: show where the run stands, don't animate. It may be hours old.
    if (animatedRoundId === null) {
      phaserBridge.sendToPhaser({
        type: 'SET_POSITION',
        position: state.position ?? 0,
      });
      // Claim this frame's roll so an old one doesn't replay.
      animatedRoll.current = rollKey;
      actions.setAnimatedRoundId(round.id);
      announce();
      return;
    }

    // A roll we have not played yet. The intro waits for the zoom.
    if (!roll || rollKey === animatedRoll.current || !introPlayed) {
      if (rollKey && rollKey !== animatedRoll.current) announce();
      return;
    }

    animatedRoll.current = rollKey;
    actions.setAnimatedRoundId(round.id);
    // Pass the lock to the animation, so the button never frees in between.
    actions.setRollInFlight(true);
    actions.setRollSubmitted(false);
    // Never leave the roll locked if the scene stops answering.
    window.clearTimeout(rollGuard.current);
    rollGuard.current = window.setTimeout(
      () => actions.setRollInFlight(false),
      ROLL_GUARD_MS
    );
    if (state.journeyId) {
      actions.recordRoll({ journeyId: state.journeyId, roll });
    }
    phaserBridge.sendToPhaser({
      type: 'PLAY_ROLL',
      dice: roll.dice,
      from: startPosition(state),
      to: state.position,
      outcome: roll.outcome,
      payout: roll.payout,
    });

    // Wait for the pawn to stop. Given up and expired are the funding card's.
    if (outcome) {
      endedRound.current = {
        roundId: round.id,
        outcome,
        totals: outcomeTotals(state, outcome === 'WON'),
      };
      announced.current = round.id;
    }
  }, [round, animatedRoundId, introPlayed, actions, colour, sceneReady]);
}
